import { forwardRef, useState } from 'react'
import type { InputHTMLAttributes } from 'react'
import { Eye, EyeOff, LockKeyhole } from 'lucide-react'
import { cx } from '@shared/lib/cx'
import { TextField } from '../Input/TextField'
import type { FieldStatus } from '../Field/field-feedback'
import {
  inputButton,
  meter,
  meterStep,
  meterStepActive,
  meterStepMedium,
  meterStepStrong,
  meterStepWeak,
  meterText,
  meterTrack,
} from './PasswordField.css'

export type PasswordStrength = {
  score: number
  label: string
}

export type PasswordFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: string
  helperText?: string
  error?: string
  status?: FieldStatus
  message?: string
  strength?: PasswordStrength
}

function getStrengthClass(score: number) {
  if (score <= 1) return meterStepWeak
  if (score <= 2) return meterStepMedium
  return meterStepStrong
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField({ strength, helperText, ...props }, ref) {
    const [visible, setVisible] = useState(false)
    const clampedScore = Math.max(0, Math.min(4, strength?.score ?? 0))
    const activeClass = getStrengthClass(clampedScore)

    return (
      <TextField
        {...props}
        ref={ref}
        type={visible ? 'text' : 'password'}
        helperText={helperText}
        leftIcon={<LockKeyhole size={17} />}
        rightSlot={
          <button
            type="button"
            className={inputButton}
            aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
            onClick={() => setVisible((current) => !current)}
          >
            {visible ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        }
        belowSlot={
          strength ? (
            <div className={meter}>
              <div className={meterTrack} aria-hidden="true">
                {[1, 2, 3, 4].map((step) => (
                  <span
                    key={step}
                    className={cx(
                      meterStep,
                      step <= clampedScore && meterStepActive,
                      step <= clampedScore && activeClass,
                    )}
                  />
                ))}
              </div>
              <span className={meterText}>{strength.label}</span>
            </div>
          ) : null
        }
      />
    )
  },
)
